import {
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { getExportStatus, startExport } from 'api';
import { FormStatus, FormTemplateType, Patient } from 'api/models';
import {
  Condition,
  ConditionType,
  FieldTemplate,
  LogicalOperator,
} from 'api/models/FieldTemplate';
import useDownloadFile from 'hooks/useDownloadHook';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ToastSeverity, projectState, toastStateSelector } from 'state';
import { GeneralIds, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';
import FieldTree from './FieldTree';
import StatusSelect from './StatusSelect';

export enum ExportDownloadFormat {
  XLS = 'xlsx',
  CSV = 'csv',
}

interface ResultListProps {
  filters: Condition[];
  fields?: FieldTemplate[];
  patientFields?: string[];
  formTemplates?: FormTemplateType[];
  patients:Patient[];
  count?:number;
}

export default function ResultList({
  filters,
  fields,
  patientFields,
  formTemplates,
  patients,
  count
}: ResultListProps) {
  const { projectId } = useParams<GeneralIds>();
  const selectedProject = useRecoilValue(projectState).selectedProject;
  const { t } = useTranslation();
  const [exportingLoading, setExportingLoading] = useState(false);
  const setToast = useSetRecoilState(toastStateSelector);
  const [format, setFormat] = useState<ExportDownloadFormat>(
    ExportDownloadFormat.XLS,
  );
  const [status, setStatus] = useState<FormStatus[]>([FormStatus.Finalized]);
  const [visitFilter, setVisitFilter] = useState('');
  const { download } = useDownloadFile();
  const [selectedFields, setSelectedFields] = useState<FieldTemplate[]>([]);

  const onExport = async () => {
    setExportingLoading(true);
    try {
      const { id } = await startExport({
        projectId,
        format,
        visitFilter,
        statusFilter: status,
        patientFilter: {
          type: ConditionType.conditionGroup,
          operation: LogicalOperator.AND,
          conditions: filters,
        },
        fields: selectedFields
          .filter(f => f?.id)
          .map(f => {
            if (f?.formTemplateId) {
              return {
                fieldTemplateId: f.id,
                formTemplateId: f?.formTemplateId,
                type: 'form',
              };
            }
            return {
              attribute: f.id,
              type: 'patient',
            };
          }),
      });

      const interval = setInterval(async () => {
        try {
          const {
            attributes: { status, url },
          } = await getExportStatus({ projectId, processId: id });
          if (status === 'completed') {
            setExportingLoading(false);
            clearInterval(interval);
            download({ fileName: 'export', url });
          }

          if (status === 'errored') {
            setExportingLoading(false);
            clearInterval(interval);
            setToast({
              isOpen: true,
              message: t('export.errors.errorOccuredDuringExport'),
              severity: ToastSeverity.error,
            });
          }
        } catch (e) {
          clearInterval(interval);
          setExportingLoading(false);
        }
      }, 2000);
    } catch (e: any) {
      console.error(e);
      const errorCode = e.response?.data?.error?.[0].code;
      setToast({
        isOpen: true,
        message: i18next.exists(`errors.${errorCode}`)
          ? t(`errors.${errorCode}`)
          : t('general.errors.export'),
        severity: ToastSeverity.error,
      });
      setExportingLoading(false);
    }
  };

  return (
    <>
      {count ? (
        <>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Typography variant="h5" mb={2}>
            {t('export.resultList')}
          </Typography>
          <Box display="flex" flexDirection={'column'}>
            <Typography variant="body1">
              {t('export.resultCount', { count })}
            </Typography>
            <Box display="flex" flexWrap={'wrap'} alignItems={'center'}>
              <Typography variant="body1" mr={1} pt={1}>
                {t('general.projectIds')}:
              </Typography>
              {patients.map(patient => (
                <Link
                  to={getRoutePath(RoutePaths.patientDetails, {
                    projectId: selectedProject?.id,
                    patientId: patient.id,
                  })}
                >
                  <Chip
                    label={patient.patientProjectId}
                    variant="outlined"
                    sx={{
                      '&:hover': {
                        background: theme => theme.palette.primary.main,
                        color: 'white',
                      },
                      mr: 1,
                      mt: 1,
                      transition: 'all 0.2s ease-in-out',
                    }}
                    color="primary"
                  />
                </Link>
              ))}
            </Box>
          </Box>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Box mt={2} display={'flex'} flexDirection={'row'}>
            <Box display="flex" flexDirection={'row'} mr={2}>
              <StatusSelect
                value={status}
                onChange={setStatus}
                loading={exportingLoading}
              />

              <TextField
                fullWidth
                sx={{ mr: 1, ml: 1 }}
                label={t('export.visitNumber')}
                disabled={exportingLoading}
                value={visitFilter}
                onChange={e => setVisitFilter(e.target.value)}
              />
            </Box>
            <FormControl>
              <FormLabel id="export-format-radio-button-group">
                {t('general.format')}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="export-format-radio-button-group"
                value={format}
                onChange={e =>
                  setFormat(e.target.value as ExportDownloadFormat)
                }
              >
                <FormControlLabel
                  disabled={exportingLoading}
                  value="xlsx"
                  control={<Radio />}
                  label="XLSX"
                />
                <FormControlLabel
                  disabled={exportingLoading}
                  value="csv"
                  control={<Radio />}
                  label="CSV"
                />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ ml: 2 }}>
              <FormLabel id="export-visits-radio-button-group">
                {t('general.visits')}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="export-visits-radio-button-group"
                value="intoNewRow"
              >
                {/* <FormControlLabel
                  disabled={exportingLoading}
                  value="intoNewColumn"
                  control={<Radio />}
                  label={t('export.visitFilter.intoNewColumn')}
                /> */}
                <FormControlLabel
                  disabled={exportingLoading}
                  value="intoNewRow"
                  control={<Radio />}
                  label={t('export.visitFilter.intoNewRow')}
                />
                {/* <FormControlLabel
                  disabled={exportingLoading}
                  value="intoNewWorkSheet"
                  control={<Radio />}
                  label={t('export.visitFilter.intoNewWorkSheet')}
                /> */}
              </RadioGroup>
            </FormControl>
          </Box>

          <FieldTree
            fields={fields}
            patientFields={patientFields}
            formTemplates={formTemplates}
            onChange={fields => setSelectedFields(fields)}
          />

          <Box
            mt={2}
            display={'flex'}
            justifyContent={'end'}
            alignItems={'end'}
          >
            <Button
              variant="contained"
              onClick={onExport}
              disabled={exportingLoading}
            >
              {exportingLoading
                ? `${t('export.export')}...`
                : t('export.export')}
            </Button>
          </Box>
          {exportingLoading && <LinearProgress sx={{ mt: 2 }} />}
        </>
      ) : (
        <>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Typography variant="h5" mt={4} width={'100%'} textAlign={'center'}>
            {t('export.noResults')}
          </Typography>
        </>
      )}
    </>
  );
}
